import { Text } from "@certn/cascadia";
import { DataDisplay } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";

import { Segment } from "./Segment";
import { getCurrencySymbol, isNotZero, numberFormatter } from "./utils";
import { InvoiceProps } from "../../../../src/schemas-and-types";

type SegmentsProps = {
  invoiceData: InvoiceProps["invoiceData"];
  isClient: boolean;
};

export function Segments(props: SegmentsProps) {
  const { invoiceData } = props;
  const currencySymbol = getCurrencySymbol(invoiceData.currency);
  const segmentEntries = Object.entries(invoiceData.segments);
  const showSegmentSubtotal = segmentEntries.length > 1;

  const segments = segmentEntries.map(([segmentKey]) => (
    <Segment
      key={segmentKey}
      segmentKey={segmentKey}
      invoiceData={invoiceData}
      isClient={props.isClient}
      showSubtotal={showSegmentSubtotal}
    />
  ));

  const creditTotal = invoiceData.credit_total;
  const hasCredits = !!creditTotal && isNotZero(creditTotal);
  const refundTotal = invoiceData.refund_total;
  const hasRefunds = !!refundTotal && isNotZero(refundTotal);

  const grossTotal = hasCredits
    ? String(parseFloat(invoiceData.total) - parseFloat(creditTotal))
    : null;

  const paymentApplications = invoiceData.payment_applications;
  const paymentRows =
    paymentApplications && paymentApplications.length > 0
      ? [
          {
            key: "payment_total",
            label: <Text style="global/button-medium">{t("Payment")}</Text>,
            value: (
              <Text style="global/button-medium">
                {currencySymbol} -
                {numberFormatter(
                  paymentApplications
                    .reduce((sum, pa) => sum + parseFloat(pa.amount), 0)
                    .toFixed(2)
                )}
              </Text>
            ),
          },
        ]
      : [];

  const refundRows = hasRefunds
    ? [
        {
          key: "refund_total",
          label: (
            <Text style="global/button-medium">{t("Refunded Amount")}</Text>
          ),
          value: (
            <Text style="global/button-medium">
              {currencySymbol} {numberFormatter(refundTotal)}
            </Text>
          ),
        },
      ]
    : [];

  const summaryRows = [
    {
      key: "subtotal",
      label: <Text style="global/button-medium">{t("Invoice Subtotal")}</Text>,
      value: (
        <Text style="global/button-medium">
          {currencySymbol}
          {numberFormatter(invoiceData.subtotal)}
        </Text>
      ),
    },
    ...(invoiceData.is_tax_exempt
      ? [
          {
            key: "tax_exempt",
            label: (
              <Text style="global/button-medium">{t("Tax Exempt (0%)")}</Text>
            ),
            value: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter("0")}
              </Text>
            ),
          },
        ]
      : invoiceData.taxes.map((tax) => ({
          key: `tax_total:${tax.name}`,
          label: <Text style="global/button-medium">{tax.name}</Text>,
          value: (
            <Text style="global/button-medium">
              {currencySymbol}
              {numberFormatter(tax.amount)}
            </Text>
          ),
        }))),
    ...paymentRows,
    ...(hasCredits
      ? [
          {
            key: "gross_total",
            label: (
              <Text style="global/button-medium">{t("Invoice Total")}</Text>
            ),
            value: grossTotal ? (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(grossTotal)}
              </Text>
            ) : null,
          },
          {
            key: "credit_total",
            label: <Text style="global/button-medium">{t("Credits")}</Text>,
            value: (
              <Text style="global/button-medium">
                {currencySymbol} {numberFormatter(creditTotal)}
              </Text>
            ),
          },
          ...refundRows,
          {
            key: "total",
            label: <Text style="global/button-medium">{t("Amount Due")}</Text>,
            value: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(invoiceData.total)}
              </Text>
            ),
          },
        ]
      : [
          ...refundRows,
          {
            key: "total",
            label: (
              <Text style="global/button-medium">{t("Invoice Total")}</Text>
            ),
            value: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(invoiceData.total)}
              </Text>
            ),
          },
        ]),
  ];

  return (
    <>
      {segments}
      <DataDisplay
        className={styles.summaryTable}
        testId="invoice-line-items"
        aria-label={t("Invoice totals")}
        stickyHeader
        renderTopMargin={false}
        columns={[
          { key: "left-pad" },
          { key: "label", isRowHeader: true },
          { key: "value" },
        ]}
        rows={summaryRows}
      />
      {invoiceData.uk_govt_admin_fee_vat_footnote ? (
        <Text
          style="global/footer-small"
          marginTop="space-4"
          testID="uk-govt-admin-fee-vat-footnote"
        >
          {invoiceData.uk_govt_admin_fee_vat_footnote}
        </Text>
      ) : null}
    </>
  );
}

const styles = {
  summaryTable: css({
    // Don't allow page breaks in the summary table
    pageBreakInside: "avoid",
    // Column widths
    "& th[data-key='left-pad']": {
      width: "auto",
    },
    "& th[data-key='label']": {
      width: "8em",
    },
    "& th[data-key='value']": {
      width: "9em",
    },
    "& td": {
      // Less padding for summary rows
      paddingTop: 2,
      paddingBottom: 2,
      // Right align labels and currency values
      textAlign: "right",
    },
    "&& thead tr": {
      borderBottomWidth: 2,
    },
    "&& tr": {
      borderBottomWidth: 0,
    },
    "& tr[data-key='total']": {
      backgroundColor: "var(--background-accent-neutral-weak)",
      borderBottomWidth: 0,
    },
    "& tr:has(+[data-key='total'])": {
      borderBottomWidth: 0,
    },
  }),
};
